import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Users, Film, Tv, Eye, Mail, 
  BarChart3, PieChart, Activity, Calendar
} from 'lucide-react';
import { createAdminService } from '../../services/api';

const AnalyticsDashboard = ({ adminAuth }) => {
  const [analytics, setAnalytics] = useState(null);
  const [emailStats, setEmailStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('overview');

  const adminService = createAdminService(adminAuth.username, adminAuth.password);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [analyticsRes, emailRes] = await Promise.all([
        adminService.getAnalyticsOverview(),
        adminService.getEmailAnalytics()
      ]);
      setAnalytics(analyticsRes.data);
      setEmailStats(emailRes.data);
    } catch (err) {
      setError('Failed to load analytics');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <BarChart3 className="text-red-500" />
          Analytics Dashboard
        </h2>
        <div className="flex gap-2">
          {['overview', 'content', 'emails'].map((view) => (
            <button
              key={view}
              onClick={() => setActiveView(view)}
              className={`px-4 py-2 rounded-lg font-medium capitalize transition-colors ${
                activeView === view
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              {view}
            </button>
          ))}
        </div>
      </div>

      {activeView === 'overview' && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <MetricCard
              icon={<Users className="text-blue-500" />}
              label="Total Viewers"
              value={analytics?.totals?.viewers || 0}
              change={`+${analytics?.this_week?.new_viewers || 0} this week`}
            />
            <MetricCard
              icon={<Mail className="text-green-500" />}
              label="Subscribers"
              value={analytics?.totals?.subscribers || 0}
              change={`+${analytics?.this_week?.new_subscribers || 0} this week`}
            />
            <MetricCard
              icon={<Film className="text-purple-500" />}
              label="Movies"
              value={analytics?.totals?.movies || 0}
            />
            <MetricCard
              icon={<Tv className="text-yellow-500" />}
              label="TV Shows"
              value={analytics?.totals?.tv_shows || 0}
            />
            <MetricCard
              icon={<Eye className="text-red-500" />}
              label="Watch Sessions"
              value={analytics?.totals?.watch_sessions || 0}
              change={`${analytics?.this_week?.watch_sessions || 0} this week`}
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Daily Views Chart */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Activity size={20} className="text-red-500" />
                Views (Last 7 Days)
              </h3>
              <div className="h-48 flex items-end justify-between gap-2">
                {analytics?.daily_views?.map((day, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div 
                      className="w-full bg-red-600 rounded-t transition-all hover:bg-red-500"
                      style={{ 
                        height: `${Math.max(4, (day.views / Math.max(...analytics.daily_views.map(d => d.views || 1))) * 150)}px` 
                      }}
                    />
                    <span className="text-gray-500 text-xs mt-2">{day.day}</span>
                    <span className="text-white text-xs font-medium">{day.views}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Genre Distribution */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <PieChart size={20} className="text-purple-500" />
                Content by Genre
              </h3>
              <div className="space-y-3">
                {analytics?.genre_distribution?.slice(0, 6).map((genre, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-24 text-gray-400 text-sm truncate">{genre.genre}</div>
                    <div className="flex-1 bg-gray-800 rounded-full h-4 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-red-600 to-red-400 rounded-full transition-all"
                        style={{ 
                          width: `${(genre.count / Math.max(...analytics.genre_distribution.map(g => g.count))) * 100}%` 
                        }}
                      />
                    </div>
                    <span className="text-white font-medium w-8 text-right">{genre.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Popular Content */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp size={20} className="text-green-500" />
              Most Popular Content
            </h3>
            {analytics?.popular_content?.length > 0 ? (
              <div className="space-y-3">
                {analytics.popular_content.map((content, i) => (
                  <div key={i} className="flex items-center gap-4 bg-gray-800/50 rounded-lg p-3">
                    <span className="text-2xl font-bold text-gray-600 w-8">#{i + 1}</span>
                    {content.thumbnail ? (
                      <img src={content.thumbnail} alt="" className="w-16 h-10 object-cover rounded" />
                    ) : (
                      <div className="w-16 h-10 bg-gray-700 rounded flex items-center justify-center">
                        <Film size={20} className="text-gray-500" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-white font-medium">{content.title || 'Unknown'}</p>
                      <p className="text-gray-500 text-sm capitalize">{content.content_type || 'content'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold">{content.views}</p>
                      <p className="text-gray-500 text-sm">views</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No watch data available yet
              </div>
            )}
          </div>
        </>
      )}

      {activeView === 'content' && (
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
          <h3 className="text-lg font-bold text-white mb-4">Subscriber Growth (30 Days)</h3>
          <div className="h-64 flex items-end gap-1">
            {analytics?.subscriber_growth?.map((day, i) => (
              <div key={i} className="flex-1 flex flex-col items-center">
                <div 
                  className="w-full bg-green-600 rounded-t transition-all hover:bg-green-500 min-h-[4px]"
                  style={{ 
                    height: `${Math.max(4, day.new_subscribers * 50)}px` 
                  }}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-between text-gray-500 text-xs mt-2">
            <span>30 days ago</span>
            <span>Today</span>
          </div>
        </div>
      )}

      {activeView === 'emails' && (
        <div className="space-y-6">
          {/* Email Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard
              icon={<Mail className="text-blue-500" />}
              label="Total Emails Sent"
              value={emailStats?.total_emails_sent || 0}
            />
            <MetricCard
              icon={<Calendar className="text-green-500" />}
              label="Total Campaigns"
              value={emailStats?.total_campaigns || 0}
            />
            <MetricCard
              icon={<TrendingUp className="text-purple-500" />}
              label="Weekly Digests"
              value={emailStats?.by_type?.weekly_digest?.count || 0}
            />
          </div>

          {/* Recent Email Logs */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
            <h3 className="text-lg font-bold text-white mb-4">Recent Email Activity</h3>
            {emailStats?.recent_logs?.length > 0 ? (
              <div className="space-y-3">
                {emailStats.recent_logs.map((log, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-800/50 rounded-lg p-3">
                    <div>
                      <p className="text-white font-medium capitalize">{log.type?.replace('_', ' ')}</p>
                      <p className="text-gray-500 text-sm">
                        {new Date(log.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 font-bold">{log.sent_count} sent</p>
                      {log.content_title && (
                        <p className="text-gray-500 text-sm">{log.content_title}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                No email activity yet
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ icon, label, value, change }) => (
  <div className="bg-gray-900 rounded-xl border border-gray-800 p-4">
    <div className="flex items-center gap-3 mb-2">
      {icon}
      <span className="text-gray-400 text-sm">{label}</span>
    </div>
    <p className="text-3xl font-bold text-white">{value.toLocaleString()}</p>
    {change && <p className="text-gray-500 text-sm mt-1">{change}</p>}
  </div>
);

export default AnalyticsDashboard;
